import React, { useState } from 'react';
import { adminApi } from '../services/api';

// Bulk void + refund for unused labels.
//
// DESIGN NOTE — why this runs orders one at a time
// ------------------------------------------------
// There is deliberately no bulk endpoint behind this. Each order goes through the existing
// POST /orders/:orderId/refund-with-void, which holds a row lock, re-checks every
// precondition, voids the label BEFORE moving money, and writes two audit events. Running
// those sequentially means:
//   - a failure on one order cannot abort or half-apply the rest;
//   - every refund keeps its own idempotency key and audit trail;
//   - we never duplicate money-handling logic into a second code path.
// The cost is wall-clock time, which is the right trade for money movement.
//
// Flow: select -> PREVIEW (read-only) -> explicit confirm -> sequential execution.

const STEP = { IDLE: 'idle', PREVIEWING: 'previewing', REVIEW: 'review', RUNNING: 'running', DONE: 'done' };

const VERDICT_STYLE = {
  ELIGIBLE: 'bg-green-50 text-green-800 border-green-200',
  INELIGIBLE: 'bg-gray-100 text-gray-700 border-gray-300',
  UNKNOWN: 'bg-amber-50 text-amber-800 border-amber-200',
};

const OUTCOME_STYLE = {
  refunded: 'bg-green-50 text-green-800 border-green-200',
  queued: 'bg-blue-50 text-blue-800 border-blue-200',
  failed: 'bg-red-50 text-red-800 border-red-200',
};

const money = (n, c = 'USD') =>
  typeof n === 'number' ? new Intl.NumberFormat('en-US', { style: 'currency', currency: c }).format(n) : '—';

export default function VoidRefundPanel({ selectedIds, onClear, onCompleted }) {
  const [step, setStep] = useState(STEP.IDLE);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const reset = () => {
    setStep(STEP.IDLE);
    setPreview(null);
    setError('');
    setResults([]);
    setProgress({ done: 0, total: 0 });
  };

  const runPreview = async () => {
    setError('');
    setStep(STEP.PREVIEWING);
    try {
      const data = await adminApi.previewVoidRefund(selectedIds);
      setPreview(data);
      setStep(STEP.REVIEW);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not build the preview. Please try again.');
      setStep(STEP.IDLE);
    }
  };

  const execute = async () => {
    const eligible = (preview?.items || []).filter((i) => i.verdict === 'ELIGIBLE');
    if (eligible.length === 0) return;

    setStep(STEP.RUNNING);
    setProgress({ done: 0, total: eligible.length });
    const out = [];

    for (const item of eligible) {
      try {
        const res = await adminApi.refundWithVoid(item.orderId, { reason: 'fraudulent' });
        if (res && res.success) {
          out.push({
            orderId: item.orderId,
            outcome: 'refunded',
            detail: `Refunded ${money(res.amountRefundedUsd, item.currency)}${
              res.customerNotified ? ' · customer emailed' : ''
            }`,
          });
        } else {
          // 202: Shippo accepted the void but has not confirmed it yet. No money has moved;
          // the refund completes on a later pass once Shippo reports SUCCESS.
          out.push({
            orderId: item.orderId,
            outcome: 'queued',
            detail: `Void ${res?.shippoRefundStatus || 'pending'} — no money moved yet; retry later to finish.`,
          });
        }
      } catch (e) {
        out.push({
          orderId: item.orderId,
          outcome: 'failed',
          detail: e?.response?.data?.message || 'Request failed.',
        });
      }
      // Update after each order so a long batch stays legible while it runs.
      setResults([...out]);
      setProgress((p) => ({ ...p, done: out.length }));
    }

    setStep(STEP.DONE);
    if (onCompleted) onCompleted();
  };

  if (!selectedIds.length && step === STEP.IDLE) return null;

  const eligible = (preview?.items || []).filter((i) => i.verdict === 'ELIGIBLE');
  const notEligible = (preview?.items || []).filter((i) => i.verdict !== 'ELIGIBLE');

  return (
    <section className="mt-6 rounded-md border border-indigo-200 bg-indigo-50 p-4" aria-live="polite">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-gray-900">
          Void &amp; refund — {selectedIds.length} label{selectedIds.length === 1 ? '' : 's'} selected
        </h2>
        <div className="flex flex-wrap gap-2">
          {step === STEP.IDLE && (
            <>
              <button
                onClick={runPreview}
                className="inline-flex min-h-[44px] items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Check eligibility
              </button>
              <button
                onClick={onClear}
                className="inline-flex min-h-[44px] items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Clear selection
              </button>
            </>
          )}
          {step === STEP.DONE && (
            <button
              onClick={() => {
                reset();
                onClear();
              }}
              className="inline-flex min-h-[44px] items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Done
            </button>
          )}
        </div>
      </div>

      <p className="mt-2 text-sm text-gray-700">
        Checking eligibility changes nothing. It reads each label&apos;s live carrier status and
        reports what Shippo would accept.
      </p>

      {error && (
        <p className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>
      )}

      {step === STEP.PREVIEWING && (
        <p className="mt-3 text-sm text-gray-700">Checking each label against Shippo…</p>
      )}

      {step === STEP.REVIEW && preview && (
        <div className="mt-4">
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Eligible" value={preview.summary.eligible} />
            <Stat label="Not eligible" value={preview.summary.ineligible} />
            <Stat label="Unreadable" value={preview.summary.unknown} />
            <Stat label="Label value" value={money(preview.summary.eligibleAmount)} />
          </dl>

          <p className="mt-3 text-sm text-gray-700">
            Refunds apply the standard fee policy, so the amount returned to each customer is
            slightly below the label value shown. A label that has actually been used will be
            rejected by Shippo at void time even if it appears eligible here.
          </p>

          {eligible.length > 0 && (
            <VerdictList title="Will be voided and refunded" items={eligible} />
          )}
          {notEligible.length > 0 && (
            <VerdictList title="Will be skipped" items={notEligible} />
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={execute}
              disabled={eligible.length === 0}
              className="inline-flex min-h-[44px] items-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Void &amp; refund {eligible.length} label{eligible.length === 1 ? '' : 's'}
            </button>
            <button
              onClick={reset}
              className="inline-flex min-h-[44px] items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {(step === STEP.RUNNING || step === STEP.DONE) && (
        <div className="mt-4">
          <p className="text-sm font-medium text-gray-900">
            {step === STEP.RUNNING
              ? `Processing ${progress.done} of ${progress.total}…`
              : `Finished — ${results.filter((r) => r.outcome === 'refunded').length} refunded, ${
                  results.filter((r) => r.outcome === 'queued').length
                } pending, ${results.filter((r) => r.outcome === 'failed').length} failed.`}
          </p>
          <ul className="mt-3 space-y-2">
            {results.map((r) => (
              <li
                key={r.orderId}
                className={`rounded-md border px-3 py-2 text-sm ${OUTCOME_STYLE[r.outcome]}`}
              >
                <span className="font-mono">{r.orderId}</span> — {r.detail}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-md border border-gray-200 bg-white p-3">
    <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
    <dd className="mt-1 text-lg font-semibold text-gray-900">{value}</dd>
  </div>
);

const VerdictList = ({ title, items }) => (
  <div className="mt-4">
    <h3 className="text-sm font-semibold text-gray-900">
      {title} ({items.length})
    </h3>
    <ul className="mt-2 space-y-2">
      {items.map((i) => (
        <li
          key={i.orderId}
          className={`rounded-md border px-3 py-2 text-sm ${VERDICT_STYLE[i.verdict] || VERDICT_STYLE.INELIGIBLE}`}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-mono">{i.orderId}</span>
            <span>{money(i.amountPaid, i.currency)}</span>
          </div>
          <p className="mt-1">{i.detail}</p>
          {i.trackingStatus && (
            <p className="mt-1 text-xs opacity-80">Carrier status: {i.trackingStatus}</p>
          )}
        </li>
      ))}
    </ul>
  </div>
);
